import type { BasicTag, ConfigAssignmentAssignmentTag } from '@arhs-fleetback/types/entities/tags';

import { FETCH_DEALER_SUCCESS } from 'constants/ActionTypes';
import type { Action } from 'modules/global/generic.types';
import { FETCH_GROUP_SUCCESS } from 'modules/groups/actionTypes';
import { EVALUATE_INTERVENTION_TAGS_SUCCESS, FETCH_INTERVENTION_SUCCESS, UPDATE_INTERVENTION_TAGS_SUCCESS } from 'modules/interventions/actionTypes';
import { createEntityAdapter, createReducer } from 'utils/ReduxUtils';

import * as types from '../actionTypes';
import type { DeleteTagSuccessAction, FetchTagsSuccessAction, StoredTag } from '../types';

type ConfigChecklist = { tags?: ConfigAssignmentAssignmentTag[] };

type DealerConfigPayload = {
  projectionType?: string;
  assignments?: {
    tags?: Record<string, Record<string, ConfigAssignmentAssignmentTag[]>>;
    extras?: Record<string, Record<string, ConfigChecklist | ConfigChecklist[]>>;
  };
};

const tagAdapter = createEntityAdapter<StoredTag>({
  selectId: ({ id }) => id,
});

export const initialState = tagAdapter.getInitialState();

type TagDataReducer = typeof initialState;

const addManyFromPage = (state: TagDataReducer, { payload }: FetchTagsSuccessAction) =>
  tagAdapter.updateMany(state, payload!.content);

const addManyFromIntervention = (state: TagDataReducer, { payload }: Action<{ tags?: BasicTag[] }>) =>
  (payload?.tags ? tagAdapter.updateMany(state, payload.tags) : state);

const removeTag = (state: TagDataReducer, action: DeleteTagSuccessAction) => tagAdapter.removeOne(state, action);

const updateAfterFetchConfig = (state: TagDataReducer, { payload }: Action<DealerConfigPayload>) => {
  const tags = Object.values(payload?.assignments?.tags ?? {}).reduce<ConfigAssignmentAssignmentTag[]>(
    (acc, assignments) => [...acc, ...Object.values(assignments).flat()],
    []
  );
  const extraTags = Object.values(payload?.assignments?.extras ?? {})
    .flatMap(byBrandId => Object.values(byBrandId))
    .flatMap(checklists => [checklists].flat().flatMap(checklist => checklist?.tags ?? []));

  return tagAdapter.updateMany(state, [...tags, ...extraTags]);
};

const CONFIG_FETCH_TYPES: string[] = [FETCH_DEALER_SUCCESS, FETCH_GROUP_SUCCESS];

const updateAfterFetchConfigMatcher = (action: Action): boolean =>
  CONFIG_FETCH_TYPES.includes(action.type) &&
  (action.payload as DealerConfigPayload)?.projectionType === 'config';

export default createReducer(
  tagAdapter.getInitialState(),
  {
    [FETCH_INTERVENTION_SUCCESS]: addManyFromIntervention,
    [UPDATE_INTERVENTION_TAGS_SUCCESS]: addManyFromIntervention,
    [EVALUATE_INTERVENTION_TAGS_SUCCESS]: addManyFromIntervention,
    [types.FETCH_TAGS_SUCCESS]: addManyFromPage,
    [types.FETCH_TAG_SUCCESS]: tagAdapter.addOne,
    [types.CREATE_TAG_SUCCESS]: tagAdapter.addOne,
    [types.UPDATE_TAG_SUCCESS]: tagAdapter.addOne,
    [types.CLONE_TAG_SUCCESS]: tagAdapter.addOne,
    [types.DELETE_TAG_SUCCESS]: removeTag,
  },
  [
    {
      matcher: updateAfterFetchConfigMatcher,
      reducer: updateAfterFetchConfig,
    },
  ]
);
